'use client';
// Shared visual primitives used by the Research Desk idea cards and Slide 9 of
// the deck. Single section/heading vocabulary — Steve directive: every research
// idea, regardless of which surface emits it, should read as the same kind of
// document.

import type { ReactNode } from 'react';

/**
 * Section wrapper. Heading sits on a left rail (md+ width) with a gold accent
 * rule, body content fills the remaining column. Stacks heading-above-body on
 * narrow viewports.
 */
export function Section({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <div className="grid md:grid-cols-[160px_1fr] gap-4 md:gap-6 py-4 border-t border-[#1e293b] first:border-t-0 first:pt-2">
      <div className="flex md:flex-col md:items-end items-baseline gap-2 md:gap-1">
        <div className="border-l-2 md:border-l-0 md:border-r-2 border-amber-400/60 pl-2 md:pl-0 md:pr-2 text-[12px] uppercase tracking-widest font-bold text-amber-300 leading-tight md:text-right">
          {heading}
        </div>
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function Stat({
  label,
  value,
  hint,
  highlight,
}: {
  label: string;
  value: string;
  hint?: string;
  highlight?: boolean;
}) {
  return (
    <div className="bg-[#0d1117] border border-[#1e293b] rounded-md px-3 py-2">
      <div className="text-[9px] uppercase tracking-wider text-[#64748b]">{label}</div>
      <div className={`tabular-nums font-bold text-[15px] ${highlight ? 'text-amber-300' : 'text-white'}`}>{value}</div>
      {hint && <div className="text-[9px] text-[#64748b] mt-0.5">{hint}</div>}
    </div>
  );
}

export function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <div className="text-[#94a3b8]">{label}</div>
      <div className="text-right tabular-nums text-[#e2e8f0]">{value}</div>
    </>
  );
}

export function Badge({ children, color }: { children: ReactNode; color: string }) {
  return (
    <span
      className="px-2 py-0.5 rounded-full font-medium"
      style={{ backgroundColor: `${color}1A`, color, border: `1px solid ${color}55` }}
    >
      {children}
    </span>
  );
}

export function ConvictionStat({ conviction }: { conviction: 'low' | 'med' | 'high' }) {
  const meta =
    conviction === 'high'
      ? { label: 'High', color: '#4ade80', bg: 'bg-emerald-500/15', border: 'border-emerald-500/40' }
      : conviction === 'low'
        ? { label: 'Low', color: '#94a3b8', bg: 'bg-zinc-700/15', border: 'border-zinc-600/40' }
        : { label: 'Med', color: '#fcd34d', bg: 'bg-amber-500/15', border: 'border-amber-500/40' };
  return (
    <div className={`rounded-md px-3 py-2 border ${meta.bg} ${meta.border}`}>
      <div className="text-[9px] uppercase tracking-wider text-[#64748b]">Conviction</div>
      <div className="tabular-nums font-bold text-[15px] uppercase" style={{ color: meta.color }}>
        {meta.label}
      </div>
    </div>
  );
}

/** Renders LLM-authored prose as a single body paragraph. Hidden if the body
 * is missing or matches the placeholder string used when the desk couldn't
 * generate the section. */
export function ProseSection({ body }: { body?: string | null }) {
  if (!body || body.startsWith('Sell-side report unavailable')) return null;
  return <div className="whitespace-pre-line text-[12.5px] leading-relaxed text-[#cbd5e1]">{body}</div>;
}
